import { ButtonBase } from "@material-ui/core"
import React, { Ref, forwardRef, memo, useEffect, useState } from "react"
import styled from "styled-components"

const StyledContainer = styled.div<{ $gap: number }>`
  display: flex;
  flex-wrap: wrap;
  /* gap: ${(props) => props.$gap}px; */
`
const StyledButtonWrap = styled(ButtonBase)<{
  $itemsPerRow: number | "auto"
  $gap: number
}>`
  width: ${(props) =>
    props.$itemsPerRow === "auto"
      ? "auto"
      : `calc(100% / ${props.$itemsPerRow} - ${props.$gap}px * (${props.$itemsPerRow} - 1) / ${props.$itemsPerRow}) `};
`
const StyledLabel = styled.label<{ $isBattery: boolean }>`
  width: 100%;
  height: 44px;
  border: solid 1px transparent;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  background-color: #fff;
  font-size: 16px;
  direction: ${(p) => (p.$isBattery ? "ltr" : "rtl")};
  cursor: pointer;
  position: relative;
  ${(p) => !p.$isBattery && "box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.1);"}

  @media print {
    display: none;
    justify-content: flex-start;
    box-shadow: none;
    background-image: none !important;

    span {
      justify-content: flex-start;
    }
  }

  b {
    font-size: 1.5em;
  }

  span {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    inset: 0;
    line-height: 1;
  }

  ${(p) =>
    p.$isBattery
      ? `
    background-image:url('/battery-disabled.svg');
    background-size:contain;
    background-repeat:no-repeat;
    border:none;
    height: 0;
    padding-bottom: calc( 100% * 59 / 232);
    position: relative;

  `
      : `background-image:none !important;`}
`

const StyledRadio = styled.input`
  position: absolute;
  border: 0;
  margin: -1px;
  padding: 0;
  border-radius: 50%;
  width: 1px;
  height: 1px;
  opacity: 0;

  :checked + ${StyledLabel} {
    border-color: ${({ theme }) => theme.colors.black};
    color: ${({ theme }) => theme.colors.black};
    background-image: url("/battery-button.svg");
    @media print {
      display: block;
      border: none;
    }
  }

  :focus + ${StyledLabel} {
    border-color: ${({ theme }) => theme.colors.black};
    color: ${({ theme }) => theme.colors.black};
    background-image: url("/battery-button.svg");

    @media print {
      display: block;
      border: none;
    }
  }

  :hover {
    border: 2px solid black;
  }

  :focus-visible {
    border: 3px solid ${(p) => p.theme.colors.darkYellow} !important;
  }
`

export type RadioItem = {
  value: string | number
  name: string
  secondaryName?: string
  radioItemMore?: any
  type?: string
  ariaLabel?: string
}

type Props = {
  id: string
  title?: string
  items: RadioItem[]
  onSelect: (value: string | number, index: number) => void
  isBattery?: boolean
  customButton?: (
    radioId: string,
    isSelected: boolean,
    item: RadioItem,
    index: number
  ) => React.ReactNode
  headlineComponent?: string
  itemsPerRow?: number | "auto"
  gap?: number
  selectedValue?: string | number
  className?: string
  allowEmpty?: boolean
  noFocus?: boolean
}

const SingleButtonSelection = forwardRef(
  (
    {
      id,
      items,
      onSelect,
      isBattery,
      customButton,
      selectedValue,
      className,
      itemsPerRow = 2,
      gap = 10,
      allowEmpty = false,
      noFocus = false,
    }: Props,
    bgRef: Ref<HTMLInputElement>
  ) => {
    const [selectedButton, setSelectedButton] = useState<string | number>(
      allowEmpty ? selectedValue : selectedValue || items[0].value
    )

    useEffect(() => {
      if (!items || !items.length || !selectedValue) {
        return
      }
      setSelectedButton(selectedValue || items[0].value)
    }, [items, selectedValue])

    const handleChange = (value: string | number, index: number) => {
      setSelectedButton(value)
      onSelect(value, index)
    }
    return (
      // <RowWithTitle {...{ title, id, headlineComponent }} noPrint={!isBattery}>
      <StyledContainer
        role="radiogroup"
        aria-labelledby={id}
        $gap={gap}
        className={className}
      >
        {items.map((item, index) => {
          const radioId = `radio_${id}_${item.value}`
          return (
            <StyledButtonWrap
              tabIndex={noFocus ? -1 : undefined}
              key={item.value}
              role="presentation"
              $itemsPerRow={itemsPerRow}
              $gap={gap}
              aria-label={
                item?.ariaLabel
                  ? item?.ariaLabel
                  : index === 0
                  ? `${item.name} כדי לבחור צבע רקע לכרטיס המתנה, נווט באמצעות החצים`
                  : item.name
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleChange(item.value, index)
                  setSelectedButton(item.value)
                }
              }}
            >
              <StyledRadio
                aria-label={
                  index === 0
                    ? `${item.name} כדי לבחור צבע רקע לכרטיס המתנה, נווט באמצעות החצים`
                    : item.name
                }
                ref={index === 0 ? bgRef : null}
                type="radio"
                name={id}
                value={item.value}
                tabIndex={!noFocus ? -1 : undefined}
                id={radioId}
                checked={item.value === selectedButton}
                // disabled={!color.details.inStock}
                onChange={() => {
                  handleChange(item.value, index)
                  setSelectedButton(item.value)
                }}
              />
              {customButton ? (
                customButton(
                  radioId,
                  item.value === selectedButton,
                  item,
                  index
                )
              ) : (
                <StyledLabel $isBattery={isBattery} htmlFor={radioId}>
                  <span>
                    {item.secondaryName && (
                      <b>{item.secondaryName.toUpperCase()}&nbsp;</b>
                    )}
                    {item.name?.toUpperCase()}
                  </span>
                </StyledLabel>
              )}
            </StyledButtonWrap>
          )
        })}
      </StyledContainer>
      // </RowWithTitle>
    )
  }
)
SingleButtonSelection.displayName = "SingleButtonSelection"
export default memo(SingleButtonSelection)
